import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ReactMarkdown from 'react-markdown';
import './Recommendations.css';
import { getRecommendations, getAllCourses, getLearningGoalsData, updateTemplateContent } from '../services/api';
import { useSelectedTemplate } from '../context/SelectedTemplateContext';
import { useNotification, NotificationContainer } from './Notification';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Set Chart.js defaults to optimize performance
ChartJS.defaults.font.family = "'Instrument Sans', sans-serif";
ChartJS.defaults.responsive = true;
ChartJS.defaults.maintainAspectRatio = false;

// Create an isolated chart component that only re-renders when necessary
const RecommendationChart = React.memo(({ chartData, chartOptions, isLoading, processedData }) => {
  const chartInstanceRef = useRef(null);
  
  // Chart Instance reference callback
  const onChartRef = useCallback(ref => {
    chartInstanceRef.current = ref;
  }, []);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Clean up chart instance on unmount
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, []);
  
  //console.log('RecommendationChart rendering'); // Debug log to track renders
  
  if (isLoading) {
    return <div className="recommendations-loading">Loading chart data...</div>;
  }
  
  if (!processedData || processedData.scores.length === 0) {
    return <div className="no-data-message">No recommendation data available</div>;
  }
  
  return (
    <Bar 
      ref={onChartRef}
      data={chartData} 
      options={chartOptions} 
      height={300}
      redraw={false}
    />
  );
});

const Recommendations = ({ courseToLGMap, allLearningGoals, coursesData, programName, isCourse }) => {
  const [recommendationData, setRecommendationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedData, setProcessedData] = useState({
    labels: [],
    scores: [],
    lgDetails: []
  });
  const [showDetailsOverlay, setShowDetailsOverlay] = useState(false);
  const [selectedLG, setSelectedLG] = useState(null);
  const [expandedRecommendations, setExpandedRecommendations] = useState({});
  const [showRubrics, setShowRubrics] = useState(true);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('2025 Spring');
  const [expandedCourseOptimizations, setExpandedCourseOptimizations] = useState({});
  const [currentLearningGoalIndex, setCurrentLearningGoalIndex] = useState({});
  const [realCoursesData, setRealCoursesData] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [learningGoalsData, setLearningGoalsData] = useState(null);
  
  // Get selected template from context
  const { selectedTemplate } = useSelectedTemplate();
  
  // Use existing notification system
  const { notifications, addNotification, removeNotification } = useNotification();
  
  // Debug log to track selectedTemplate changes
  useEffect(() => {
    console.log('Recommendations - selectedTemplate changed:', selectedTemplate);
  }, [selectedTemplate]);
  
  //console.log('Recommendations component rendering'); // Debug log to track renders

  // Fetch real courses data and learning goals
  useEffect(() => {
    const fetchCoursesAndLearningGoals = async () => {
      try {
        setCoursesLoading(true);
        const school = new URLSearchParams(window.location.search).get('school') || 'MARYLAND';
        
        // Fetch courses list
        const coursesResponse = await getAllCourses(school, isCourse ? undefined : programName);
        const courseList = coursesResponse?.course_list || [];
        
        if (courseList.length === 0) {
          setRealCoursesData([]);
          return;
        }
        
        // Fetch learning goals data
        // Validate template ID - only use valid template IDs (1, 2, 3, etc.) and fallback to 1
        const templateId = (selectedTemplate?.id && selectedTemplate.id > 0 && selectedTemplate.id < 100) ? selectedTemplate.id : 1;
        console.log('Recommendations - Using template ID:', templateId, 'from selectedTemplate:', selectedTemplate);
        const learningGoalsResponse = await getLearningGoalsData(templateId, courseList);
        setLearningGoalsData(learningGoalsResponse);
        

        
        // Extract learning goals data directly from the API response
        
        // Aggregate all learning goals data across all courses
        const aggregatedLearningGoals = {};
        
        // First, collect all unique learning goal IDs
        const allLGIds = new Set();
        courseList.forEach(courseCode => {
          const courseData = learningGoalsResponse[courseCode] || {};
          Object.keys(courseData).forEach(lgId => allLGIds.add(lgId));
        });
        
        // Process each learning goal using the data from the API
        Array.from(allLGIds).forEach(lgId => {
          const originalScores = [];
          const optimizedScores = [];
          
          // STEP 1: Get descriptions directly from the API response for this specific learning goal
          // We need to find ANY course that has data for this learning goal to get the descriptions
          let learningGoalData = null;
          let foundCourseCode = null;
          
          // Find the first course that has data for this learning goal
          for (const courseCode of courseList) {
            const courseData = learningGoalsResponse[courseCode] || {};
            if (courseData[lgId]) {
              learningGoalData = courseData[lgId];
              foundCourseCode = courseCode;
              break;
            }
          }
          
          // Get descriptions from the found data
          const description = learningGoalData?.description || "No description available";
          
          // Check if optimized_description has meaningful content (not just header text)
          let optimizedDescription = "No optimization available";
          if (learningGoalData?.optimized_description) {
            const optDesc = learningGoalData.optimized_description.trim();
            // Check if it's more than just the header text
            if (optDesc && optDesc !== "**Optimized Learning Goal Description:**" && optDesc !== "**Optimized Learning Goal Description:**\n") {
              optimizedDescription = optDesc;
            }
          }
          
          // STEP 2: Collect scores from ALL courses for this learning goal (aggregation for scores only)
          courseList.forEach(courseCode => {
            const courseData = learningGoalsResponse[courseCode] || {};
            if (courseData[lgId]) {
              const originalScore = courseData[lgId].original_score || 0;
              const optimizedScore = courseData[lgId].optimized_score;
              
              // For program assessment, include ALL scores (including zeros) to get correct average
              // For course assessment, only include positive scores
              if (isCourse) {
                if (originalScore > 0) originalScores.push(originalScore);
                if (optimizedScore !== null && optimizedScore > 0) optimizedScores.push(optimizedScore);
              } else {
                // Include all scores including zeros for program-level aggregation
                originalScores.push(originalScore);
                if (optimizedScore !== null) {
                  optimizedScores.push(optimizedScore);
                } else {
                  optimizedScores.push(originalScore); // Use original score if no optimization
                }
              }
            } else if (!isCourse) {
              // For program assessment, if a course has no data for this learning goal, 
              // it should contribute a score of 0 to the aggregation
              originalScores.push(0);
              optimizedScores.push(0);
            }
          });
          
          // Debug logging for program assessment to show course contributions
          if (!isCourse) {
            console.log(`Learning Goal ${lgId} - Course Contributions:`, {
              lgId,
              courseList,
              originalScores,
              optimizedScores,
              avgOriginalScore: originalScores.length > 0 
                ? originalScores.reduce((sum, score) => sum + score, 0) / originalScores.length 
                : 0
            });
          }
          
          // Calculate average scores (aggregation for scores only)
          const avgOriginalScore = originalScores.length > 0 
            ? originalScores.reduce((sum, score) => sum + score, 0) / originalScores.length 
            : 0;
          
          const avgOptimizedScore = optimizedScores.length > 0 
            ? optimizedScores.reduce((sum, score) => sum + score, 0) / optimizedScores.length 
            : avgOriginalScore;
          
          // Extract numeric ID from lgId (e.g., "LG1" -> 1, "LG2" -> 2)
          const numericId = parseInt(lgId.replace(/[^0-9]/g, ''));
          
          aggregatedLearningGoals[lgId] = {
            id: lgId,
            numericId: numericId, // Add numeric ID for API calls
            current: description,        // Unique description per learning goal
            suggested: optimizedDescription, // Unique optimized description per learning goal
            originalScore: avgOriginalScore,    // Aggregated score across courses
            optimizedScore: avgOptimizedScore   // Aggregated score across courses
          };
        });
        
        // Calculate overall scores based on whether this is a single course or program assessment
        let overallCurrentScore, overallPotentialScore, overallImprovement;
        
        // Debug logging for program assessment
        if (!isCourse) {
          console.log('Program Assessment - Aggregated Learning Goals:', aggregatedLearningGoals);
          console.log('Program Assessment - All Original Scores:', Object.values(aggregatedLearningGoals).map(lg => lg.originalScore));
        }
        
        if (isCourse) {
          // For single course: use programName as key to find course data in API response
          const courseData = learningGoalsResponse[programName] || {};
          
          // Extract scores from the course data
          const courseOriginalScores = Object.values(courseData)
            .map(lg => lg.original_score || 0);
          
          const courseOptimizedScores = Object.values(courseData)
            .map(lg => lg.optimized_score || 0);
          
          // Calculate average including all learning goals (even those with 0 scores)
          overallCurrentScore = courseOriginalScores.length > 0 
            ? courseOriginalScores.reduce((sum, score) => sum + score, 0) / courseOriginalScores.length 
            : 0;
          
          overallPotentialScore = courseOptimizedScores.length > 0 
            ? courseOptimizedScores.reduce((sum, score) => sum + score, 0) / courseOptimizedScores.length 
            : overallCurrentScore;
        } else {
          // For program assessment: aggregate scores across all courses
          // Include ALL learning goals with their actual scores (including zeros)
          const allOriginalScores = Object.values(aggregatedLearningGoals)
            .map(lg => lg.originalScore);
          
          const allOptimizedScores = Object.values(aggregatedLearningGoals)
            .map(lg => lg.optimizedScore);
          
          // Calculate average including all learning goals (even those with 0 scores)
          overallCurrentScore = allOriginalScores.length > 0 
            ? allOriginalScores.reduce((sum, score) => sum + score, 0) / allOriginalScores.length 
            : 0;
          
          overallPotentialScore = allOptimizedScores.length > 0 
            ? allOptimizedScores.reduce((sum, score) => sum + score, 0) / allOptimizedScores.length 
            : overallCurrentScore;
          
          // Debug logging for program assessment scores
          if (!isCourse) {
            console.log('Program Assessment - Final Scores:', {
              allOriginalScores,
              allOptimizedScores,
              overallCurrentScore,
              overallPotentialScore
            });
          }
        }
        
        overallImprovement = overallPotentialScore > overallCurrentScore 
          ? `+${(overallPotentialScore - overallCurrentScore).toFixed(1)}`
          : '+0.0';
        
        // Create single aggregated course object
        const aggregatedCourse = {
          id: 1,
          courseCode: programName || (isCourse ? courseList[0] : 'Program Assessment'),
          courseName: "", 
          currentScore: overallCurrentScore.toFixed(1),
          potentialScore: overallPotentialScore.toFixed(1),
          improvement: overallImprovement,
          learningGoals: Object.values(aggregatedLearningGoals)
        };
        
        setRealCoursesData([aggregatedCourse]);
      } catch (error) {
        console.error('Error fetching courses and learning goals:', error);
        // Fallback to empty array if API fails
        setRealCoursesData([]);
      } finally {
        setCoursesLoading(false);
      }
    };

    fetchCoursesAndLearningGoals();
  }, [selectedTemplate, programName, isCourse]);

  // Fetch recommendation data
  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setIsLoading(true);
        
        // Clear previous recommendation data when template changes
        setRecommendationData(null);
        setProcessedData({
          labels: [],
          scores: [],
          lgDetails: []
        });
        
        // Prepare details object from courseToLGMap
        const details = {};

        console.log('CourseToLGMap:', courseToLGMap);
        console.log('coursesData:', coursesData);
        console.log('courseToLGMap entries count:', Object.entries(courseToLGMap).length);

        Object.entries(courseToLGMap).forEach(([courseId, lgArray]) => {
          // Find the course name from coursesData
          const course = coursesData.find(c => c.id === courseId);
          
          // Log each course's lgArray and its length
          console.log(`Course ID: ${courseId}, Course Name: ${course?.name || 'NOT FOUND'}, lgArray:`, lgArray, `Length: ${lgArray.length}`);
          
          if (course && lgArray.length > 0) {
            // Handle both formats: main LGs (1, 2) and sub LGs (1.1, 1.2)
            const lgNumbers = lgArray.map(lg => {
              // Remove "LG" prefix if present
              const cleanLg = String(lg).replace(/^LG\s*/i, '');
              // Try to parse as a number
              return cleanLg;
            }).filter(lg => lg);
            
            //console.log(`Course ${course.name} (ID: ${courseId}) - Original LGs:`, lgArray);
            //console.log(`Course ${course.name} - Processed LG numbers:`, lgNumbers);
            
            if (lgNumbers.length > 0) {
              details[course.name] = lgNumbers;
            }
          }
        });

        console.log('Final API request details:', details);
        console.log('Details object keys count:', Object.keys(details).length);
        
        // Only make API call if we have details
        if (Object.keys(details).length > 0) {
          // Validate template ID - only use valid template IDs (1, 2, 3, etc.) and fallback to 1
          const templateId = (selectedTemplate?.id && selectedTemplate.id > 0 && selectedTemplate.id < 100) ? selectedTemplate.id : 1;
          console.log('Recommendations - Using template ID for recommendations:', templateId, 'from selectedTemplate:', selectedTemplate);
          const data = await getRecommendations(templateId, details);
          //console.log('Recommendation API response:', data);
          setRecommendationData(data);
        } else {
          console.log('No details to send to API - skipping get-recommendation call');
        }
      } catch (err) {
        console.error('Error fetching recommendations:', err);
        setError('Failed to load recommendations');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchRecommendations();
  }, [courseToLGMap, coursesData, selectedTemplate]);
  
  // Process recommendation data once received
  useEffect(() => {
    if (!recommendationData) return;
    
    try {
      // Calculate average scores for each LG across all courses
      const lgScores = {};
      const lgRecommendations = {};
      
      // Collect all scores and recommendations by LG
      Object.entries(recommendationData).forEach(([courseName, courseData]) => {
        if (courseData.overall_score) {
          Object.entries(courseData.overall_score).forEach(([lg, score]) => {
            // Convert null scores to 0
            const validScore = score !== null && score !== undefined ? score : 0;
            if (!lgScores[lg]) {
              lgScores[lg] = [];
            }
            lgScores[lg].push(validScore);
          });
        }
        
        // Process recommendations - handle different possible data structures
        const processRecommendations = () => {
          // Check if we have the new structure with concise_recommendation
          if (courseData.concise_recommendation) {
            Object.entries(courseData.concise_recommendation).forEach(([lg, recommendation]) => {
              if (!lgRecommendations[lg]) {
                lgRecommendations[lg] = {};
              }
              lgRecommendations[lg][courseName] = {
                concise: recommendation,
                full: courseData.full_recommendation?.[lg] || ''
              };
            });
          } 
          // Fallback to old structure if needed
          else if (courseData.recommendation) {
          Object.entries(courseData.recommendation).forEach(([lg, recommendation]) => {
            if (!lgRecommendations[lg]) {
              lgRecommendations[lg] = {};
            }
              lgRecommendations[lg][courseName] = {
                concise: recommendation,
                full: ''
              };
          });
        }
        };
        
        processRecommendations();
      });
      
      // Calculate averages and prepare data for chart
      const labels = [];
      const scores = [];
      const lgDetails = [];
      
      Object.entries(lgScores).forEach(([lg, scoreArray]) => {
        const avgScore = scoreArray.reduce((sum, score) => sum + score, 0) / scoreArray.length;
        
        // Find learning goal description from selectedTemplate
        let description = `Learning Goal ${lg}`;
        let title = `Learning Goal ${lg}`;
        
        if (selectedTemplate && selectedTemplate.LG) {
          // Find the matching learning goal in the template
          const templateLG = selectedTemplate.LG.find(templateLg => {
            // Handle different formats of learning goal IDs in template
            let templateLgId;
            if (templateLg.title.includes('LG')) {
              // If it has "LG" prefix, extract the number
              templateLgId = templateLg.title.replace(/LG\s*/, '');
            } else if (templateLg.id) {
              // Use ID if available
              templateLgId = templateLg.id.toString();
            } else {
              // Otherwise use the title directly
              templateLgId = templateLg.title;
            }
            
            // Compare with the LG from the recommendation data
            // Handle both formats: with and without decimal points
            return templateLgId === lg || 
                  // For main LGs (e.g., "1" should match "1.0")
                  (lg.includes('.') && lg.split('.')[0] === templateLgId) ||
                  // For main LGs where recommendation data uses the integer (e.g., "1" should match "1")
                  (!lg.includes('.') && templateLgId === lg);
          });
          
          if (templateLG) {
            description = templateLG.description;
            // Format the title consistently
            if (templateLG.title.includes('LG')) {
              title = templateLG.title; // Use the original title with LG prefix
            } else {
            // Use full name instead of abbreviation
            title = `Learning Goal ${lg}`;
            }
          }
        }
        
        // Find courses with this LG
        const coursesWithLg = [];
        Object.entries(recommendationData).forEach(([courseName, courseData]) => {
          if (courseData.overall_score && courseData.overall_score[lg] !== undefined) {
            // Convert null score to 0
            const score = courseData.overall_score[lg] !== null ? courseData.overall_score[lg] : 0;
            
            // Get recommendations in the appropriate format
            let conciseRec = '';
            let fullRec = '';
            
            if (courseData.concise_recommendation && courseData.concise_recommendation[lg]) {
              conciseRec = courseData.concise_recommendation[lg];
            } else if (courseData.recommendation && courseData.recommendation[lg]) {
              conciseRec = courseData.recommendation[lg];
            }
            
            if (courseData.full_recommendation && courseData.full_recommendation[lg]) {
              fullRec = courseData.full_recommendation[lg];
            }
            
            coursesWithLg.push({
              name: courseName,
              score: `${score.toFixed(2)}`,
              conciseRecommendation: conciseRec,
              fullRecommendation: fullRec
            });
          }
        });
        
        labels.push(title);
        scores.push(normalizeScoreToRubricLevel(avgScore));
        
        // Sort by LG number for consistent order
        lgDetails.push({
          id: lg,
          title,
          score: avgScore,
          description,
          courses: coursesWithLg
        });
      });
      
      // Sort lgDetails by id
      lgDetails.sort((a, b) => {
        // Extract numeric parts for comparison
        const getNumericValue = (id) => {
          // For IDs with decimal points (e.g., "1.2")
          if (id.includes('.')) {
            const [major, minor] = id.split('.');
            return parseFloat(major) * 100 + parseFloat(minor);
          }
          // For IDs without decimal points (e.g., "1")
          return parseFloat(id) * 100;
        };
        
        return getNumericValue(a.id) - getNumericValue(b.id);
      });
      
      setProcessedData({
        labels,
        scores,
        lgDetails
      });
      
    } catch (err) {
      console.error('Error processing recommendation data:', err);
      setError('Failed to process recommendations');
    }
  }, [recommendationData, selectedTemplate]);

  // Function to normalize scores to rubric levels (1-4)
  const normalizeScoreToRubricLevel = (score) => {
    if (score < 25) return 1; // Covered Incidentally
    if (score < 50) return 2; // Introduced
    if (score < 75) return 3; // Reinforced
    return 4; // Emphasized
  };

  // Get color based on rubric level
  const getBarColor = useCallback((level) => {
    if (level >= 4) return '#2D6E5B'; // Green - Emphasized
    if (level >= 3) return '#a57f42'; // Orange - Reinforced
    if (level >= 2) return '#ba3e28'; // Red - Introduced
    return '#8B5CF6'; // Purple - Covered Incidentally
  }, []);
  
  // Memoize chart data and options to prevent unnecessary recalculations
  const { chartData, chartOptions } = useMemo(() => {
    // Prepare data for chart
    const chartData = {
      labels: processedData.labels,
      datasets: [
        {
          data: processedData.scores,
          backgroundColor: processedData.scores.map(level => getBarColor(level)),
          borderWidth: 0,
          borderRadius: 3,
          barPercentage: 0.7,
        }
      ]
    };
  
    // Chart options
    const chartOptions = {
      indexAxis: 'y', // Horizontal bar chart
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 1000,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          titleColor: '#333',
          bodyColor: '#333',
          borderColor: '#ddd',
          borderWidth: 1,
          cornerRadius: 4,
          caretSize: 6,
          boxPadding: 6,
          displayColors: false,
          callbacks: {
            title: (tooltipItems) => {
              return tooltipItems[0].label;
            },
            label: (context) => {
              const level = context.raw;
              const levelNames = {
                1: 'Covered Incidentally',
                2: 'Introduced', 
                3: 'Reinforced',
                4: 'Emphasized'
              };
              return `Coverage: ${levelNames[level] || 'Unknown'}`;
            }
          }
        }
      },
      scales: {
        y: {
          grid: {
            display: false,
            drawBorder: false
          },
          ticks: {
            font: {
              size: 12,
              weight: 500
            },
            color: '#333',
            padding: 20,
            callback: function(value, index) {
              const label = this.getLabelForValue(index);
              // Truncate long labels for the chart but keep full in tooltip
              if (label.length > 16) {
                return label.substr(0, 14) + '...';
              }
              return label;
            }
          }
        },
        x: {
          beginAtZero: true,
          min: 0,
          max: 4,
          grid: {
            color: '#ddd',
            borderDash: [5, 5],
            drawBorder: false,
          },
          ticks: {
            font: {
              size: 12,
            },
            color: '#595959',
            stepSize: 1,
            callback: function(value) {
              const levelNames = {
                0: '',
                1: 'Incidental',
                2: 'Introduced',
                3: 'Reinforced',
                4: 'Emphasized'
              };
              return levelNames[value] || '';
            }
          }
        }
      }
    };
    
    return { chartData, chartOptions };
  }, [processedData, getBarColor]);
  
  const handleShowDetails = useCallback((lg) => {
    setSelectedLG(lg);
    setShowDetailsOverlay(true);
  }, []);
  
  const handleCloseDetails = useCallback(() => {
    setShowDetailsOverlay(false);
    setSelectedLG(null);
    setExpandedRecommendations({});
  }, []);
  
  const toggleFullRecommendation = useCallback((courseIndex) => {
    setExpandedRecommendations(prev => ({
      ...prev,
      [courseIndex]: !prev[courseIndex]
    }));
  }, []);

  const toggleRubrics = useCallback(() => {
    setShowRubrics(!showRubrics);
  }, [showRubrics]);



  const handleYearChange = useCallback((event) => {
    setSelectedAcademicYear(event.target.value);
  }, []);

  const toggleCourseOptimization = useCallback((courseId) => {
    setExpandedCourseOptimizations(prev => ({
      ...prev,
      [courseId]: !prev[courseId]
    }));
    
    // Initialize learning goal index if not exists
    if (!currentLearningGoalIndex[courseId]) {
      setCurrentLearningGoalIndex(prev => ({
        ...prev,
        [courseId]: 0
      }));
    }
  }, [currentLearningGoalIndex]);

  const handleLearningGoalNavigation = useCallback((courseId, direction) => {
    const course = realCoursesData.find(c => c.id === courseId);
    if (!course) return;
    
    const maxIndex = course.learningGoals.length - 1;
    setCurrentLearningGoalIndex(prev => {
      const currentIndex = prev[courseId] || 0;
      let newIndex = currentIndex;
      
      if (direction === 'next' && currentIndex < maxIndex) {
        newIndex = currentIndex + 1;
      } else if (direction === 'prev' && currentIndex > 0) {
        newIndex = currentIndex - 1;
      }
      
      return {
        ...prev,
        [courseId]: newIndex
      };
    });
  }, [realCoursesData]);

  const handleApplyChanges = useCallback(async (courseId, learningGoalId) => {
    try {
      // Find the course data
      const course = realCoursesData.find(c => c.id === courseId);
      if (!course) {
        console.error('Course not found:', courseId);
        return;
      }

      // Get the current learning goal being displayed
      const currentLGIndex = currentLearningGoalIndex[courseId] || 0;
      const currentLG = course.learningGoals[currentLGIndex];
      
      if (!currentLG) {
        console.error('Learning goal not found for course:', courseId);
        return;
      }

      // Get school parameter from URL
      const urlParams = new URLSearchParams(window.location.search);
      const school = urlParams.get('school') || 'MARYLAND';

      // Get the current value from the textarea
      const textareaElement = document.querySelector('.suggested-learning-goal');
      const editedDescription = textareaElement ? textareaElement.value : currentLG.suggested;

      // Prepare the update payload
      const updates = [{
        template_type: "parent",
        learning_goal_id: parseInt(learningGoalId), // Use the passed learningGoalId parameter
        description: editedDescription
      }];

      console.log('Applying changes:', { school, updates, learningGoalId });

      // Call the API
      await updateTemplateContent(school, updates);
      
      console.log('Successfully applied changes for learning goal:', learningGoalId);
      
      // Show success notification using existing system
      addNotification('Updated successfully!', 'success');
      
    } catch (error) {
      console.error('Error applying changes:', error);
      // You could add an error notification here
      // addNotification('Failed to apply changes', 'error');
    }
  }, [realCoursesData, currentLearningGoalIndex]);

  const handleApplyAllChanges = useCallback(async (courseId) => {
    try {
      // Find the course data
      const course = realCoursesData.find(c => c.id === courseId);
      if (!course) {
        console.error('Course not found:', courseId);
        return;
      }

      // Get school parameter from URL
      const urlParams = new URLSearchParams(window.location.search);
      const school = urlParams.get('school') || 'MARYLAND';

      // Collect all learning goals from the current course
      const updates = course.learningGoals.map(learningGoal => ({
        template_type: "parent",
        learning_goal_id: learningGoal.numericId,
        description: learningGoal.suggested
      }));

      console.log('Applying all changes:', { school, updates });

      // Call the API to update all learning goals
      await updateTemplateContent(school, updates);
      
      console.log('Successfully applied all changes for course:', courseId);
      
      // Show success notification using existing system
      addNotification('Updated successfully!', 'success');
      
    } catch (error) {
      console.error('Error applying all changes:', error);
      // You could add an error notification here
      // addNotification('Failed to apply all changes', 'error');
    }
  }, [realCoursesData]);

  if (error) {
    return <div className="recommendations-error">{error}</div>;
  }

  return (
    <div className="recommendations-container">
      <div className="recommendations-header">
        <div className="rubrics-toggle">
          <label className="switch">
            <input 
              type="checkbox" 
              checked={showRubrics}
              onChange={toggleRubrics}
            />
            <span className="slider round"></span>
          </label>
          <span className="toggle-label">Show Rubrics</span>
        </div>
      </div>
      
      {showRubrics && (
        <div className="rubrics-panel">
          <h3>Coverage Level</h3>
          <div className="rubric-items">
            <div className="rubric-item">
              <div className="rubric-color incidental-score"></div>
              <div className="rubric-label">&lt; 25: Covered Incidentally</div>
            </div>
            <div className="rubric-item">
              <div className="rubric-color low"></div>
              <div className="rubric-label">25-49: Introduced</div>
            </div>
            <div className="rubric-item">
              <div className="rubric-color medium"></div>
              <div className="rubric-label">50-74: Reinforced</div>
            </div>
            <div className="rubric-item">
              <div className="rubric-color high"></div>
              <div className="rubric-label">≥ 75: Emphasized</div>
            </div>
          </div>
        </div>
      )}
      
      <div className="recommendations-chart-section">
        <div className="recommendations-chart-container">
          <RecommendationChart 
            chartData={chartData}
            chartOptions={chartOptions}
            isLoading={isLoading}
            processedData={processedData}
          />
        </div>
      </div>
      
      <div className="recommendations-details">
        {processedData.lgDetails.map((lg) => (
          <div key={lg.id} className="recommendation-card">
            <div className="recommendation-header">
              <div className="recommendation-lg">{lg.title}</div>
              <div className={`recommendation-score ${lg.score >= 75 ? 'high-score-1' : lg.score >= 50 ? 'medium-score-1' : lg.score >= 25 ? 'low-score-1' : 'incidental-score'}`}>
                {lg.score.toFixed(2)}
              </div>
            </div>
            <div className="recommendation-subtitle">Avg. Coverage Level</div>
            
            <div className="recommendation-content">
              <p className="recommendation-description">
                {lg.description}
              </p>
              
              
              <div className="recommendation-courses">
                {lg.courses.map((course, idx) => (
                  <div key={idx} className="course-item">
                    <span className="course-name">{course.name}</span>
                    <span className="course-score">{course.score}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="recommendation-footer">
              <button 
                className="show-details-button" 
                onClick={() => handleShowDetails(lg)}
              >
                <div className="recommendation-icon">💡</div>
                <div className="recommendation-text">Show Recommendations</div>
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {      /* Optimize Learning Goals Section */}
      <div className="optimize-learning-goals-section">
        <div className="optimize-header">
          <div className="optimize-title">
            <img 
              src="/target-icon.svg" 
              alt="Target icon" 
              className="optimize-icon"
              width="24" 
              height="24"
            />
            Optimize Learning Goals - {programName || (isCourse ? 'Course Assessment' : 'Program Assessment')}
          </div>
          <div className="academic-year-dropdown">
            <select 
              className="year-select"
              value={selectedAcademicYear}
              onChange={handleYearChange}
            >
              <option value="2025 Spring">Academic Year: <strong>2025 Spring</strong></option>
              <option value="2024 Fall">Academic Year: <strong>2024 Fall</strong></option>
              <option value="2024 Spring">Academic Year: <strong>2024 Spring</strong></option>
              <option value="2023 Fall">Academic Year: <strong>2023 Fall</strong></option>
            </select>
          </div>
        </div>
        
        <div className="potential-impact-panel">
          <div className="potential-impact-content">
            <div className="potential-impact-left">
              <div className="potential-impact-header">
                <div className="potential-impact-text">
                  <img 
                    src="/icons8-idea.svg" 
                    alt="Idea icon" 
                    className="impact-icon"
                    width="29" 
                    height="29"
                  />
                  <div className="impact-title">Potential Impact</div>
                </div>
              </div>
              <div className="impact-description">By optimizing your learning goals, you could improve your overall program alignment score by <strong>{realCoursesData[0]?.improvement || '0.0'} points</strong>.</div>
            </div>
            <button className="apply-all-button" onClick={handleApplyAllChanges}>
              Apply All Changes
            </button>
          </div>
        </div>
        
        <div className="course-optimization-list">
          {coursesLoading ? (
            <div className="recommendations-loading">Loading courses...</div>
          ) : realCoursesData.length === 0 ? (
            <div className="no-data-message">No courses available</div>
          ) : realCoursesData.map((course) => {
            const isExpanded = expandedCourseOptimizations[course.id];
            const currentLGIndex = currentLearningGoalIndex[course.id] || 0;
            const currentLG = course.learningGoals[currentLGIndex];
            const totalLGs = course.learningGoals.length;
            
            return (
              <div key={course.id} className="course-optimization-card">
                <div 
                  className="course-optimization-header"
                  onClick={() => toggleCourseOptimization(course.id)}
                >
                  <div className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                  </div>
                  <div className="course-info">
                    <h3 className="course-title">{course.courseCode}</h3>
                  </div>
                  
                  <div className="score-section">
                    <div className="score-item">
                      <span className="score-label">Current Score</span>
                      <div className="score-value current-score">{course.currentScore}</div>
                    </div>
                    
                    <div className="score-item">
                      <span className="score-label">Potential Score</span>
                      <div className="potential-score-container">
                        <div className="score-value potential-score">{course.potentialScore}</div>
                        <div className="score-improvement">
                          <span>{course.improvement}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="course-optimization-content">
                    <div className="optimization-content-wrapper">
                      <div className="refinements-header">
                        <div className="learning-goal-title">
                          Suggested Learning Goal Refinements
                        </div>
                        <div className="refinements-controls">
                          <button 
                            className="apply-all-button"
                            onClick={() => handleApplyAllChanges(course.id)}
                          >
                            Apply All Changes
                          </button>
                          <div className="pagination-controls">
                            <button 
                              className="pagination-btn"
                              onClick={() => handleLearningGoalNavigation(course.id, 'prev')}
                              disabled={currentLGIndex === 0}
                            >
                              ◀
                            </button>
                            <span className="pagination-info">{currentLGIndex + 1} / {totalLGs}</span>
                            <button 
                              className="pagination-btn"
                              onClick={() => handleLearningGoalNavigation(course.id, 'next')}
                              disabled={currentLGIndex === totalLGs - 1}
                            >
                              ▶
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="learning-goal-id-header">
                        Learning Goal {currentLG.id}
                      </div>
                      
                      <div className="current-label">Current</div>
                      <div className="current-learning-goal">
                        {currentLG.current}
                      </div>
                      
                      <div className="suggested-label">Suggested Enhancement</div>
                      <textarea 
                        className="suggested-learning-goal"
                        value={currentLG.suggested}
                        rows={4}
                        onChange={(e) => {
                          // Update the currentLG.suggested value when user types
                          const updatedLearningGoals = [...course.learningGoals];
                          updatedLearningGoals[currentLGIndex] = {
                            ...updatedLearningGoals[currentLGIndex],
                            suggested: e.target.value
                          };
                          
                          // Update the course data
                          const updatedCourses = realCoursesData.map(c => 
                            c.id === course.id 
                              ? { ...c, learningGoals: updatedLearningGoals }
                              : c
                          );
                          setRealCoursesData(updatedCourses);
                        }}
                      />
                      

                      
                      <div className="individual-apply-section">
                        <button 
                          className="apply-changes-button"
                          onClick={() => {
                            console.log('currentLG object:', currentLG);
                            console.log('currentLG.id:', currentLG.id);
                            console.log('currentLG.numericId:', currentLG.numericId);
                            console.log('currentLG keys:', Object.keys(currentLG));
                            handleApplyChanges(course.id, currentLG.numericId);
                          }}
                        >
                          Apply Changes
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {showDetailsOverlay && selectedLG && (
        <div className="rec-details-overlay">
          <div className="rec-details-modal">
            <div className="rec-modal-header">
              <h2>{selectedLG.title} Recommendations</h2>
              <button className="rec-close-button" onClick={handleCloseDetails}>×</button>
            </div>
            <div className="rec-modal-content">
              <div className="rec-table-container">
                <table className="rec-table">
                  <colgroup>
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '55%' }} />
                    <col style={{ width: '15%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Score</th>
                      <th>Discovery</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedLG.courses.map((course, idx) => (
                      <React.Fragment key={idx}>
                        <tr>
                        <td>{course.name}</td>
                        <td className={`${parseFloat(course.score) >= 75 ? 'high-score-1' : parseFloat(course.score) >= 50 ? 'medium-score-1' : parseFloat(course.score) >= 25 ? 'low-score-1' : 'incidental-score'}`}>
                          {course.score}
                        </td>
                          <td className="rec-concise-cell">
                            {course.conciseRecommendation || 'No recommendation available'}
                          </td>
                          <td className="rec-action-cell">
                            {course.fullRecommendation && (
                              <button 
                                className={`view-more-button-alt ${expandedRecommendations[idx] ? 'expanded' : ''}`}
                                onClick={() => toggleFullRecommendation(idx)}
                                aria-expanded={expandedRecommendations[idx] ? 'true' : 'false'}
                                aria-controls={`details-section-${idx}`}
                              >
                                {expandedRecommendations[idx] ? 'Hide Details' : 'View More'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {expandedRecommendations[idx] && course.fullRecommendation && (
                          <tr className="full-recommendation-row" id={`details-section-${idx}`}>
                            <td colSpan="4" className="full-recommendation-cell">
                              <div 
                                className="full-recommendation-header"
                                id={`details-${idx}`}
                              >
                                Detailed Recommendation
                              </div>
                              <div 
                                className="markdown-content"
                                aria-labelledby={`details-${idx}`}
                              >
                                {typeof course.fullRecommendation === 'object' ? 
                                  // If fullRecommendation is an object with numbered keys
                                  Object.entries(course.fullRecommendation).map(([key, value]) => (
                                    <ReactMarkdown key={key}>{value}</ReactMarkdown>
                                  ))
                                  : 
                                  // If fullRecommendation is a string
                                  <ReactMarkdown>{course.fullRecommendation}</ReactMarkdown>
                                }
                              </div>
                            </td>
                      </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Notification Container for displaying notifications */}
      <NotificationContainer 
        notifications={notifications} 
        removeNotification={removeNotification} 
      />
    </div>
  );
};

// Custom equality check for React.memo
const recommendationsPropsAreEqual = (prevProps, nextProps) => {
  // Log props changes to help debug re-renders
  const isCoursesToLGMapEqual = JSON.stringify(prevProps.courseToLGMap) === JSON.stringify(nextProps.courseToLGMap);
  const isCoursesDataEqual = prevProps.coursesData === nextProps.coursesData;
  const isLearningGoalsEqual = prevProps.allLearningGoals === nextProps.allLearningGoals;
  const isProgramNameEqual = prevProps.programName === nextProps.programName;
  const isCourseEqual = prevProps.isCourse === nextProps.isCourse;
  
  //console.log('Props comparison:', { 
  //  isCoursesToLGMapEqual, 
  //  isCoursesDataEqual,
  //  isLearningGoalsEqual,
  //  isProgramNameEqual,
  //  isCourseEqual
  //});
  
  // Only re-render if these props changed
  return isCoursesToLGMapEqual && isCoursesDataEqual && isLearningGoalsEqual && isProgramNameEqual && isCourseEqual;
};

// Wrap the component with React.memo with custom equality check
export default React.memo(Recommendations, recommendationsPropsAreEqual); 